import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Report, ReportStatus } from '../../types';
import StatusBadge from '../StatusBadge/StatusBadge';
import { safeFormatDate } from '../../utils/dateUtils';
import { getFailureDisplayItems, getUnclassifiedFailureLabel } from '../../utils/failureClassification';
import './ReportTable.css';

type SortColumn = 'number' | 'status' | 'customer' | 'countryCode' | 'technician' | 'startDate' | null;
type SortDirection = 'asc' | 'desc' | null;

interface ReportTableProps {
  reports: Report[];
  onReportOpen: (reportId: string) => void;
}

export const ReportTable: React.FC<ReportTableProps> = ({
  reports,
  onReportOpen,
}) => {
  const { t, i18n } = useTranslation();
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);

  const handleRowClick = (report: Report): void => {
    if (report.status === ReportStatus.CANCELED) return;
    onReportOpen(report.id);
  };

  const handleExpandClick = (e: React.MouseEvent, reportId: string): void => {
    e.stopPropagation();
    setExpandedReportId(expandedReportId === reportId ? null : reportId);
  };

  const calculateSparePartsPercentage = (report: Report): number | null => {
    const allParts = report.machines?.flatMap((m) => m.spareParts || []) || [];
    if (allParts.length === 0) return null;
    const totalQuantity = allParts.reduce((sum, p) => sum + (p.quantity || 0), 0);
    if (totalQuantity === 0) return null;
    const totalUsed = allParts.reduce((sum, p) => sum + (p.used || 0), 0);
    return totalUsed / totalQuantity;
  };

  const formatPercentage = (value: number | null): string => {
    if (value === null) return 'N/A';
    return (value * 100).toFixed(2) + '%';
  };

  const handleSort = (column: SortColumn): void => {
    if (sortColumn === column) {
      // Toggle direction or reset to natural order
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // Reset to natural order
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      // New column, start with ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedReports = (): Report[] => {
    if (!sortColumn || !sortDirection) {
      return reports; // Natural order from backend
    }

    const sorted = [...reports].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortColumn) {
        case 'number':
          aValue = a.number;
          bValue = b.number;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'customer':
          aValue = (a.customerName || '').toLowerCase();
          bValue = (b.customerName || '').toLowerCase();
          break;
        case 'technician':
          aValue = (a.technicianName || '').toLowerCase();
          bValue = (b.technicianName || '').toLowerCase();
          break;
        case 'startDate':
          aValue = a.startTime ? new Date(a.startTime).getTime() : 0;
          bValue = b.startTime ? new Date(b.startTime).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const renderSortIcon = (column: SortColumn): React.ReactElement | null => {
    if (sortColumn !== column) {
      return <span className="sort-icon sort-icon-default">⇅</span>;
    }
    if (sortDirection === 'asc') {
      return <span className="sort-icon sort-icon-asc">↑</span>;
    }
    if (sortDirection === 'desc') {
      return <span className="sort-icon sort-icon-desc">↓</span>;
    }
    return null;
  };

  if (reports.length === 0) {
    return (
      <div className="reports-table-container">
        <div className="no-results">{t('mainForm.noResults')}</div>
      </div>
    );
  }

  const sortedReports = getSortedReports();

  return (
    <div className="reports-table-container">
      <table className="reports-table">
        <thead>
          <tr>
            <th className="sortable" onClick={() => handleSort('number')}>
              {t('reportTable.number')} {renderSortIcon('number')}
            </th>
            <th className="sortable" onClick={() => handleSort('status')}>
              {t('reportTable.status')} {renderSortIcon('status')}
            </th>
            <th className="sortable" onClick={() => handleSort('customer')}>
              {t('reportTable.customer')} {renderSortIcon('customer')}
            </th>
            <th>{t('reportTable.countryCode')}</th>
            <th className="sortable" onClick={() => handleSort('technician')}>
              {t('reportTable.technician')} {renderSortIcon('technician')}
            </th>
            <th className="sortable" onClick={() => handleSort('startDate')}>
              {t('reportTable.startDate')} {renderSortIcon('startDate')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedReports.map((report) => (
            <React.Fragment key={report.id}>
              <tr
                className={`${expandedReportId === report.id ? 'expanded-row' : ''} ${report.status === ReportStatus.CANCELED ? 'row-canceled' : ''}`}
                onClick={() => handleRowClick(report)}
                style={{ cursor: report.status === ReportStatus.CANCELED ? 'default' : 'pointer' }}
              >
                <td data-label={t('reportTable.number')}>
                  <span
                    className={`expand-icon ${expandedReportId === report.id ? 'expanded' : ''}`}
                    onClick={(e) => handleExpandClick(e, report.id)}
                    role="button"
                    aria-label={expandedReportId === report.id ? t('common.collapse') : t('common.expand')}
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleExpandClick(e as unknown as React.MouseEvent, report.id); }}
                  >
                    ▶
                  </span>
                  {report.number}
                </td>
                <td data-label={t('reportTable.status')}>
                  <StatusBadge status={report.status as ReportStatus} />
                </td>
                <td data-label={t('reportTable.customer')}>{report.customerName || '-'}</td>
                <td data-label={t('reportTable.countryCode')}>{report.customerCountryCode || '-'}</td>
                <td data-label={t('reportTable.technician')}>{report.technicianName || '-'}</td>
                <td data-label={t('reportTable.startDate')}>{safeFormatDate(report.startTime, 'dd/MM/yyyy')}</td>
              </tr>
              {expandedReportId === report.id && (
                <tr className="accordion-row">
                  <td colSpan={6}>
                    <div className="accordion-content">
                      <div className="accordion-grid">
                        <div className="accordion-item">
                          <label>{t('reportTable.number')}:</label>
                          <span>{report.number}</span>
                        </div>
                        <div className="accordion-item">
                          <label>{t('reportTable.status')}:</label>
                          <StatusBadge status={report.status as ReportStatus} />
                        </div>
                        <div className="accordion-item accordion-item-machines">
                          <label>{t('mainForm.machines')}:</label>
                          {report.machines && report.machines.length > 0 ? (
                            <ul className="machines-list-preview">
                              {report.machines.map((m, index) => (
                                <li key={index}>
                                  {m.description}
                                  {report.status === ReportStatus.CLOSED && (
                                    <ul className="machine-failures-preview">
                                      {m.failureClassifications && m.failureClassifications.length > 0 ? (
                                        m.failureClassifications.map((f, fIndex) => {
                                          const items = getFailureDisplayItems(f, i18n.language);
                                          return (
                                            <li key={fIndex} className="failure-preview-item">
                                              {items.length > 0 ? (
                                                items.map((item, i) => (
                                                  <React.Fragment key={i}>
                                                    <span title={item.raw ?? undefined}>{item.label}</span>
                                                    {i < items.length - 1 ? ' · ' : ''}
                                                  </React.Fragment>
                                                ))
                                              ) : (
                                                getUnclassifiedFailureLabel(m.serviceType, t)
                                              )}
                                            </li>
                                          );
                                        })
                                      ) : (
                                        <li className="failure-preview-item">{getUnclassifiedFailureLabel(m.serviceType, t)}</li>
                                      )}
                                    </ul>
                                  )}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                        <div className="accordion-item">
                          <label>{t('mainForm.sparePartsUsage')}:</label>
                          <span className="spare-parts-percentage">
                            {formatPercentage(calculateSparePartsPercentage(report))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};
